/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

// Function to determine text color (black or white) based on background color brightness
const getContrastTextColor = (backgroundColor) => {
  try {
    // Remove opacity suffix if present (e.g., "CC" from hex color)
    const cleanColor = backgroundColor.replace(/[0-9A-F]{2}$/i, '');
    
    // Ensure we have a valid 6-character hex color
    if (!/^#[0-9A-F]{6}$/i.test(cleanColor)) {
      console.warn('Invalid hex color format:', backgroundColor);
      return '#000000'; // Default to black for invalid colors
    }
    
    // Convert hex to RGB
    const hex = cleanColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light backgrounds, white for dark backgrounds
    // Lowered threshold to 0.1 to better capture bright colors
    return luminance > 0.6 ? '#000000' : '#ffffff';
  } catch (error) {
    return '#000000'; // Default to black on error
  }
};

const MultiLineChart = ({
  datasets,
  labels,
  title,
  height = 260,
  isPercentage = false,
}) => {
  if (!datasets || datasets.length === 0 || !labels || labels.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <span style={{ color: "#666" }}>No data available</span>
      </div>
    );
  }

  const chartData = {
    labels: labels,
    datasets: datasets,
  };

  // Calculate the maximum value from all datasets to determine consistent formatting
  const maxValue = Math.max(
    ...datasets.flatMap((dataset) =>
      dataset.data.filter((val) => val !== null),
    ),
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: -5,
        left: -5,
        right: 5,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "#555555",
          font: {
            size: 12,
          },
          usePointStyle: true,
          pointStyle: "line",
        },
      },
      title: {
        display: false, // We'll handle title manually
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#ffffff",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            if (value === null || value === undefined) return null;
            if (isPercentage) {
              return `${label}: ${value.toFixed(1)}`;
            }
            // Format large numbers dynamically based on magnitude
            if (value >= 1000000000) {
              return `${label}: ${(value / 1000000000).toFixed(1)}`;
            } else if (value >= 1000000) {
              return `${label}: ${(value / 1000000).toFixed(1)}`;
            } else if (value >= 1000) {
              return `${label}: ${(value / 1000).toFixed(1)}`;
            } else {
              return `${label}: ${value.toFixed(0)}`;
            }
          },
        },
      },
      datalabels: {
        display: true,
        color: function (context) {
          const baseColor = context.dataset.borderColor || "#ffffff";
          const backgroundColor = baseColor + "CC"; // Match the actual background color
          return getContrastTextColor(backgroundColor);
        },
        font: {
          size: 10,
          weight: "bold",
        },
        backgroundColor: function (context) {
          const color = context.dataset.borderColor || "#ffffff";
          // Add opacity by converting to rgba
          return color + "CC"; // CC = 80% opacity in hex
        },
        borderRadius: 4,
        padding: {
          top: 1,
          bottom: 1,
          left: 3,
          right: 3,
        },
        anchor: "center",
        align: "center",
        offset: 0,
        formatter: function (value, context) {
          if (value === null || value === undefined) return "";
          if (isPercentage) {
            return value.toFixed(1);
          }
          // Format based on the maximum value's magnitude (consistent with Y-axis)
          if (maxValue >= 1000000000) {
            return (value / 1000000000).toFixed(1);
          } else if (maxValue >= 1000000) {
            return (value / 1000000).toFixed(1);
          } else if (maxValue >= 1000) {
            return (value / 1000).toFixed(1);
          } else {
            return value.toFixed(0);
          }
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: "#555555",
          font: {
            size: 12,
          },
          maxRotation: 0,
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        position: "left",
        grid: {
          color: "rgba(85, 85, 85, 0.1)",
        },
        ticks: {
          color: "#555555",
          font: {
            size: 12,
          },
          padding: 15,
          callback: function (value) {
            if (isPercentage) {
              return value.toFixed(1);
            }

            // Format based on the maximum value's magnitude
            if (maxValue >= 1000000000) {
              return (value / 1000000000).toFixed(1);
            } else if (maxValue >= 1000000) {
              return (value / 1000000).toFixed(1);
            } else if (maxValue >= 1000) {
              return (value / 1000).toFixed(1);
            } else {
              return value.toFixed(0);
            }
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.2,
        borderWidth: 2,
      },
      point: {
        radius: 2,
        hoverRadius: 4,
      },
    },
  };

  return (
    <div style={{ height, position: "relative", marginBottom: "20px", backgroundColor: "white" }}>
      {/* Custom title layout */}
      {title && (
        <div
          style={{
            position: "absolute",
            top: "-5px",
            left: "8px",
            right: "8px",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              color: "#555555",
              fontSize: "14px",
            }}
          >
            {title}
          </div>
        </div>
      )}
      <Line data={chartData} options={options} />
    </div>
  );
};

MultiLineChart.propTypes = {
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number),
      borderColor: PropTypes.string,
      backgroundColor: PropTypes.string,
      borderWidth: PropTypes.number,
      fill: PropTypes.bool,
      tension: PropTypes.number,
      pointRadius: PropTypes.number,
      pointHoverRadius: PropTypes.number,
    }),
  ),
  labels: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  height: PropTypes.number,
  isPercentage: PropTypes.bool,
};

MultiLineChart.defaultProps = {
  title: "",
  height: 260,
};

export default MultiLineChart;
